import { useNavigate } from 'react-router-dom';
import type { DiscoveryItem } from '@/mocks/discoverySocial';
import { typeColors, typeIcons, directionConfig } from '@/mocks/discoverySocial';

interface DiscoveryCardProps {
  item: DiscoveryItem;
}

export default function DiscoveryCard({ item }: DiscoveryCardProps) {
  const navigate = useNavigate();
  const colors = typeColors[item.type];
  const dir = directionConfig[item.trendDirection];

  const formatNumber = (n: number): string => {
    if (n >= 1000000) return `${(n / 1000000).toFixed(1)}M`;
    if (n >= 1000) return `${(n / 1000).toFixed(1)}K`;
    return `${n}`;
  };

  return (
    <article
      className={`group bg-background-50 rounded-xl border ${colors.border}/30 overflow-hidden cursor-pointer transition-all duration-200 hover:border-foreground-300/50`}
      onClick={() => item.link && navigate(item.link)}
    >
      {/* Image */}
      <div className="relative aspect-[4/3] overflow-hidden">
        <img
          src={item.thumbnailUrl}
          alt={item.name}
          className="w-full h-full object-cover object-top transition-transform duration-300 group-hover:scale-105"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-black/50 via-transparent to-transparent"></div>

        {/* Type badge */}
        <div className="absolute top-3 left-3">
          <span className={`inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-[10px] font-bold ${colors.bg} ${colors.text} backdrop-blur-sm`}>
            <i className={`${typeIcons[item.type]}`}></i>
            {item.type === 'radios' ? 'Radio' : item.type === 'locutores' ? 'Locutor' : item.type === 'programas' ? 'Programa' : item.type === 'eventos' ? 'Evento' : 'Concurso'}
          </span>
        </div>

        {/* Trend badge */}
        <div className="absolute top-3 right-3">
          <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-bold bg-background-50/90 ${dir.colorClass}`}>
            <i className={`${dir.icon} text-[10px]`}></i>
            {item.growthPercent >= 50 ? `+${item.growthPercent}%` : `+${item.growthPercent}%`}
          </span>
        </div>

        {/* Score bar */}
        <div className="absolute bottom-3 left-3 right-3">
          <div className="flex items-center gap-2">
            <div className="flex-1 h-1 bg-background-50/30 rounded-full overflow-hidden">
              <div
                className={`h-full ${colors.bar} rounded-full transition-all duration-500`}
                style={{ width: `${item.trendScore}%` }}
              ></div>
            </div>
            <span className="text-[10px] font-bold text-background-50/90">{item.trendScore}</span>
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="p-4">
        <h3 className="text-sm font-bold text-foreground-950 leading-snug mb-1 line-clamp-1 group-hover:text-primary-600 transition-colors">
          {item.name}
        </h3>
        <p className="text-xs text-foreground-500 leading-relaxed line-clamp-1 mb-3">
          {item.subtitle}
        </p>

        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <span className="text-[10px] font-semibold text-foreground-400">{item.country}</span>
            <span className="w-1 h-1 rounded-full bg-foreground-300"></span>
            <span className="text-[10px] font-semibold text-foreground-400">{item.category}</span>
          </div>
          <span className={`inline-flex items-center gap-0.5 text-[10px] font-bold ${dir.colorClass}`}>
            <i className={`${dir.icon} text-[10px]`}></i>
            {dir.label}
          </span>
        </div>

        {/* Badge */}
        <div className="mt-3 pt-3 border-t border-background-200/70 flex items-center justify-between">
          <span className="text-[10px] font-semibold text-foreground-400 uppercase tracking-wider">{item.badge}</span>
          <span className="text-[10px] font-semibold text-foreground-500">
            {formatNumber(item.followers)} seguidores
          </span>
        </div>
      </div>
    </article>
  );
}